<template>
  <div
    class="bg-white border-0 d-flex justify-content-between align-items-center cursor-pointer"
    @click="toggle_tab('Forms')"
  >
  </div>

  <div class="collapse" :class="{ 'show': active_tabs.includes('Forms') }">
    <div class="mt-2">
      <div class="border-0 p-2 rounded-lg">
        
        <!-- Header -->
        <div class="compliance-monitoring-program-header" @click.stop="toggle_program('trust-form')">
          <div class="d-flex justify-content-between align-items-start w-100 mb-2">
            <div class="d-flex align-items-start">
              <i
                class="bi mt-1 text-dark-blue fs-5 fw-bold"
                :class="is_trust_form_open ? 'bi-chevron-down text' : 'bi-chevron-right'"
              ></i>
              <div class="ms-2 mt-1">
                <i class="bx bx-folder-open text-dark-blue fs-4"></i>
              </div>
              <p class="mb-0 text-xs fw-bold ms-2 mt-2">{{ form_name }}</p>
            </div>
            <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-sm text-dark mt-2" data-bs-toggle="modal" data-bs-target="#verification_logs" 
            @click.stop="open_verification_logs">
              Verification Logs
            </button>
          </div>
        </div>

        <!-- Services Card -->
        <div v-if="is_trust_form_open" class="compliance-monitoring-projects-wrapper">
          <!-- Service Selection -->
          <div class="card border rounded-lg shadow mb-4 ms-4">
            <div class="card-body">
              <p class="mb-3 text-xs fw-bold">Select Service To View Results :</p>
              <div class="row">
                <div v-for="service in verification_services" :key="service.id" class="col-md-6 mb-2">
                  <div class="form-check">
                    <input 
                      class="form-check-input" 
                      type="radio" 
                      :id="`service-${service.id}`"
                      :value="service.id" 
                      v-model="selected_service_id"
                      @change="on_service_change"
                    >
                    <label class="form-check-label text-xxs" :for="`service-${service.id}`">
                      <strong>{{ service.name }}</strong> - {{ service.description }}
                    </label>
                  </div>
                </div>
              </div>

              <!-- Loading State -->
              <div v-if="loading_results" class="mt-2">
                <div class="spinner-border spinner-border-sm" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <span class="ms-2 text-xxs">Loading {{ get_selected_service_name }} results...</span>
              </div>
            </div>
          </div>

          <!-- Results Section -->
          <div
            class="compliance-monitoring-project-accordion border border-2 rounded-lg mb-3 ms-4 shadow"
            v-for="(result, index) in processed_verification_results"
            :key="result.key || index"
          >
            <!-- Card Header -->
            <div class="compliance-monitoring-project-header" @click="toggle_expanded(index)">
              <div class="compliance-monitoring-project-connector"></div>
              <div class="d-flex align-items-center">
                <i class="bi" :class="expanded_index === index ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                <div class="ms-2">
                  <i class="bi bi-file-earmark-text fs-4"></i>
                </div>
                <div class="ms-3">
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <p class="mb-0 text-xs">{{ result.label }}</p>
                    <span class="status-badge" :class="get_status_badge_class(result.status)">
                      {{ result.status }}
                    </span>
                  </div>
                  <p class="mb-0 text-xxs text-muted">{{ result.date }}</p>
                </div>
              </div>
              <div class="d-flex justify-content-end">
                  <button
                    class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-sm text-dark"
                    data-bs-toggle="modal"
                    data-bs-target="#verification_report_modal"
                    @click.stop="open_report_modal(result)"
                  >
                    View Report
                  </button>


              </div>
            </div>

            <!--Field Results + Details -->
            <div class="w-100 px-3 pb-3 mt-3">
              <!-- FAIL CASE -->
              <div v-if="result.status === 'fail'" class="w-100">

                <!-- Alert -->
                <div
                  v-if="expanded_index === index"
                  class="alert alert-danger rounded-4 shadow-sm p-3 text-center mb-3"
                >
                  <i class="bi bi-x-circle me-2"></i>
                  <span class="fw-bold text-xs">
                    {{ result.message || 'System verification failed.' }}
                  </span>
                </div>

                <!--Field Results (standalone white card) -->
                <div
                  v-if="result.details && result.details.field_results"
                  class="card border-0 rounded-4 shadow-sm bg-white p-3 w-100 mb-3"
                >
                  <p class="fw-bold text-xs text-muted mb-3">Field Results</p>

                  <div
                    v-for="(field, key) in result.details.field_results"
                    :key="key"
                    class="field-result-item d-flex align-items-center justify-content-between mb-2"
                  >
                    <div class="d-flex align-items-center">
                      <i
                        class="me-2 fs-5"
                        :class="field.match ? 'bi bi-check-circle text-success' : 'bi bi-x-circle text-danger'"
                      ></i>
                      <span class="fw-semibold text-xs text-dark">{{ format_key(key) }}:</span>
                    </div>
                    <span
                      class="fw-bold text-xs text-end"
                      :class="field.match ? 'text-success' : 'text-danger'"
                    >
                      {{ field.user_answer }}
                    </span>
                  </div>
                </div>
              </div>

              <!--SUCCESS CASE -->
              <div
                v-else-if="result.status === 'success' && result.details && result.details.field_results"
                class="w-100"
              >
                <!--Field Results (standalone white card) -->
                <div class="card border-0 rounded-4 bg-white p-3 w-100 mb-0">
                  <div class="d-flex align-items-center justify-content-between mb-3">
                    <p class="fw-bold text-xs text-muted mb-0">Field Results</p>
                  </div>

                  <div
                    v-for="(field, key) in result.details.field_results"
                    :key="key"
                    class="field-result-item d-flex align-items-center justify-content-between mb-2"
                  >
                    <div class="d-flex align-items-center">
                      <i
                        class="me-2 fs-5"
                        :class="field.match ? 'bi bi-check-circle text-success' : 'bi bi-x-circle text-danger'"
                      ></i>
                      <span class="fw-semibold text-xs text-dark">{{ format_key(key) }}:</span>
                    </div>
                    <span
                      class="fw-bold text-xs text-end"
                      :class="field.match ? 'text-success' : 'text-danger'"
                    >
                      {{ field.user_answer }}
                    </span>
                  </div>
                </div>

                <!-- AML-Specific Display (only shows when supplier_data exists) -->
                <div
                  v-if="expanded_index === index && is_aml_service(result) && result.details.aml_check_results && result.details.aml_check_results.total_matches > 0"
                  class="card border-0 rounded-4 bg-white p-4 w-100 mt-0"
                >
                  <!-- AML Check Results Section -->
                  <div v-if="result.details.aml_check_results">
                    
                    <!-- Risk Score Badge -->
                    <div class="alert rounded-4 p-2 mb-3 text-center"
                        :class="get_risk_alert_class(result.details.risk_score)">
                      <i class="bi bi-shield-exclamation me-2"></i>
                      <span class="fw-bold text-xs">Overall Risk: {{ result.details.risk_score }}</span>
                    </div>

                    <!-- Summary Stats -->
                    <div class="row mb-3">
                      <div class="col-md-4 mb-2">
                        <div class="bg-light border rounded-3 p-3 text-center">
                          <p class="text-xxs text-muted mb-1">Total Matches</p>
                          <p class="text-lg fw-bold mb-0">{{ result.details.aml_check_results.total_matches }}</p>
                        </div>
                      </div>
                      <div class="col-md-4 mb-2">
                        <div class="bg-light border rounded-3 p-3 text-center">
                          <p class="text-xxs text-muted mb-1">Highest Risk Score</p>
                          <p class="text-lg fw-bold mb-0">{{ result.details.aml_check_results.highest_risk_score }}%</p>
                        </div>
                      </div>
                      <div class="col-md-4 mb-2">
                        <div class="bg-light border rounded-3 p-3 text-center">
                          <p class="text-xxs text-muted mb-1">Categories</p>
                          <p class="text-lg fw-bold mb-0">{{ result.details.aml_check_results.categories_summary.length }}</p>
                        </div>
                      </div>
                    </div>

                    <!-- All Matches Details -->
                    <div class="bg-light border rounded-3 p-3 mb-3">
                      <p class="fw-bold text-xs text-dark mb-3">Detailed Matches</p>
                      
                      <div 
                        v-for="(match, idx) in result.details.aml_check_results.all_matches" 
                        :key="idx"
                        class="card border mb-2 p-2"
                      >
                        <div class="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <p class="text-xs fw-bold mb-1">{{ match.entity_name }}</p>
                            <span class="status-badge status-badge success text-xxs">{{ match.entity_type }}</span>
                          </div>
                        </div>
                        
                        <div class="row">
                          <div class="col-md-6 mb-1">
                            <span class="text-xxs text-muted">Category:</span>
                            <p class="text-xxs mb-0">{{ match.category }}</p>
                          </div>
                          <div class="col-md-6 mb-1">
                            <span class="text-xxs text-muted">Date Listed:</span>
                            <p class="text-xxs mb-0">{{ match.date_listed }}</p>
                          </div>
                          <div class="col-md-6 mb-1" v-if="match.gender !== 'N/A'">
                            <span class="text-xxs text-muted">Gender:</span>
                            <p class="text-xxs mb-0">{{ match.gender }}</p>
                          </div>
                          <div class="col-md-6 mb-1" v-if="match.date_of_birth !== 'N/A'">
                            <span class="text-xxs text-muted">Date of Birth:</span>
                            <p class="text-xxs mb-0">{{ match.date_of_birth }}</p>
                          </div>
                          <div class="col-12 mb-1" v-if="match.occupation !== 'N/A'">
                            <span class="text-xxs text-muted">Occupation:</span>
                            <p class="text-xxs mb-0">{{ match.occupation }}</p>
                          </div>
                          <div class="col-12">
                            <span class="text-xxs text-muted">Summary:</span>
                            <p class="text-xxs mb-0">{{ match.summary }}</p>
                          </div>
                          <div class="col-12">
                            <span class="text-xxs text-muted">Comments:</span>
                            <p class="text-xxs mb-0">{{ match.comment }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Verification Details Card (shows for non-AML OR AML with no supplier data) -->
                <div
                  v-if="expanded_index === index && result.details.results_data && (!is_aml_service(result) || (is_aml_service(result) && (!result.details.aml_check_results || result.details.aml_check_results.total_matches === 0)))"
                  class="card border-0 rounded-4 shadow-sm bg-white p-4 w-100"
                >
                  <p class="fw-bold text-xs text-muted mb-3">Verification Details</p>

                  <!-- Loop through main results -->
                  <div
                    v-for="(value, key) in result.details.results_data"
                    :key="key"
                    class="bg-light border rounded-3 p-3 mb-3 border-0"
                  >
                    <p class="fw-bold text-xs text-dark mb-1">{{ format_key(key) }} :</p>

                    <!-- If nested object -->
                    <template v-if="typeof value === 'object' && value !== null">
                      <!-- Array -->
                      <div v-if="Array.isArray(value)">
                        <div
                          v-for="(item, i) in value"
                          :key="i"
                          class="border rounded-3 p-3 bg-light mb-2"
                        >
                          <div
                            v-for="(sub_value, sub_key) in item"
                            :key="sub_key"
                            class="mb-2"
                          >
                            <strong class="d-block text-xs text-muted">
                              {{ format_key(sub_key) }} :
                            </strong>
                            <span class="text-xs text-dark">
                              {{ format_value(sub_value) }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Nested Object -->
                      <div v-else>
                        <div
                          v-for="(sub_value, sub_key) in value"
                          :key="sub_key"
                          class="border rounded-3 p-3 bg-white mb-2"
                        >
                          <strong class="d-block text-xs text-muted mb-1">
                            {{ format_key(sub_key) }} :
                          </strong>
                          <span class="text-xs text-dark">
                            {{ format_value(sub_value) }}
                          </span>
                        </div>
                      </div>
                    </template>

                    <!-- If simple value -->
                    <template v-else>
                      <p class="text-xs text-dark mb-0">{{ format_value(value) }}</p>
                    </template>
                  </div>
                </div>
              </div>
              </div>
            </div>
            <!-- Empty State -->
            <div
            v-if="processed_verification_results.length === 0 && selected_service_id && !loading_results"
            class="text-center p-4 border rounded-lg mt-3 ms-3 shadow"
          >
            <i class="bi bi-inbox fs-1 text-muted"></i>
            <p class="text-muted mt-2 mb-0 text-xs">
              No verification results found for {{ get_selected_service_name }}.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Verification logs Modal -->
   <teleport to="body">
    <div class="modal fade" id="verification_logs" tabindex="-1" aria-labelledby="verification_logs_label" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content bg-white rounded-3 p-0 shadow-lg">
        <div class="modal-header border-bottom-0 cute-header shadow-lg">
          <p class="modal-title text-white text-xxs" id="verification_logs_label">Verification logs</p>
          <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="card shadow-sm border table-border-color rounded-lg p-3 table-responsive text-xs">
              <table id="verification_logs_table" class="table table-hover">
                  <thead class="custom-header">
                    <tr>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start ps-3">Service Name</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start ps-3">Status</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start ps-3">Message</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start ps-3">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="log in verification_logs" :key="log.id">
                      <td class="text-xxs text-start">{{ get_service_name(log.service_id) }}</td>
                      <td class="text-xxs text-start">{{ log.status }}</td>
                      <td class="text-xxs text-start text-wrap">
                        {{ get_display_message(log.message) }}
                      </td>
                      <td class="text-xxs text-start">{{ format_date(log.created_at) }}</td>
                    </tr>
                  </tbody>
              </table>
            </div>
        </div>
      </div>
    </div>
    </div>
   </teleport>
   <!-- reports modal -->
   <teleport to="body">
      <div
        class="modal fade"
        id="verification_report_modal"
        tabindex="-1"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
          <div class="modal-content bg-white rounded-4 shadow-lg">

            <!-- Header -->
            <div class="modal-header border-bottom-0">
              <h6 class="modal-title fw-bold text-xs">
                Verification Report
              </h6>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>

            <!-- Body -->
            <div class="modal-body p-4">

              <!-- SAFETY CHECK -->
              <KYCReport
                v-if="selected_report"
                :report="map_report_data(selected_report)"
              />

            </div>
          </div>
        </div>
      </div>
    </teleport>
</template>

<script>
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  GET_APPLICATION_FORMS,
  get_headers,
  GET_PROJECTS_ASSIGNED_TO_SERVICE,
  GET_VERIFICATION_LOGS
} from '@/utils/constants/constants.js';
import axios from 'axios';
import { getKycVerificationResults } from "@/services/verification/integrated_verification_apis/kyc_verification";
import { getAMLVerificationResults } from "@/services/verification/integrated_verification_apis/aml_verification";
import { getIDVVerificationResults } from "@/services/verification/integrated_verification_apis/idv_verification";
import { getCIPCVerificationResults } from "@/services/verification/integrated_verification_apis/cipc_verification";
import { getCreditReportVerificationResults } from "@/services/verification/integrated_verification_apis/credit_report_verification";
import KYCReport from '../Reports/verification/KYCReport.vue';
import 'datatables.net';
import 'datatables.net-dt/css/dataTables.dataTables.css';
import $ from 'jquery';
import formatting_mixins from '@/utils/mixins/formatting_mixins';

export default {
  name: "VerificationResults",
  mixins: [alert_mixin, formatting_mixins],
  components: {
    KYCReport
  },
  props: {
    application_id: {
      type: [String, Number],
      required: true
    },
    entity_profile_id: {
      type: [String, Number],
      default: null
    },
    external_results: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    headers: get_headers(),
    expanded_index: null,
    active_tabs: ['Forms'],
    is_trust_form_open: true,
    form_name: '',
    verification_services: [],
    api_verification_results: [],
    loading_results: false,
    loading_services: false,
    selected_service_id: null,
    project_id: sessionStorage.getItem('project_id'),
    verification_logs: [],
    view_report_index: null,
    selected_report: null,
  }),

  computed: {
    processed_verification_results() {
      const results = this.external_results.length > 0 ? this.external_results : this.api_verification_results;

      return results.map(result => ({
        key: result.key || result.label || result.service_name || `result-${Math.random()}`,
        label: result.label || result.service_name,
        date: result.date || result.timestamp || new Date().toLocaleDateString(),
        status: this.normalize_status(result.status),
        details: result.details || result.data || result,
        component: result.component,
      }));
    },

    resolved_entity_profile_id() {
      const route_entity_id = this.$route?.params?.entity_profile_id;
      if (route_entity_id) return route_entity_id;
      if (this.entity_profile_id) return this.entity_profile_id;
      return sessionStorage.getItem('entity_profile_id');
    },

    get_selected_service_name() {
      if (!this.selected_service_id) return '';
      const service = this.verification_services.find(s => s.id == this.selected_service_id);
      return service ? service.name : `Service ${this.selected_service_id}`;
    },

    selected_service() {
      if (!this.selected_service_id) return null;
      return this.verification_services.find(s => s.id == this.selected_service_id);
    }
  },

  async mounted() {
    await this.fetch_verification_services();
    if (this.application_id){
      this.get_forms();
    }
    this.get_verification_logs()
  },

  methods: {
    open_report_modal(result) {
      this.selected_report = result;
    },

    toggle_view_report(index) {
      this.view_report_index =
      this.view_report_index === index ? null : index;
    },

    map_report_data(result) {
      const data = result.details?.results_data || result.details || {};

      return {
        generated_at: data.generated_at || new Date().toLocaleString(),
        report_type: data.report_type || result.label,
        reference_number: data.reference_number || "N/A",

        consumer: data.consumer || {},
        contact: data.contact || {},
        employment_history: data.employment_history || [],
        fraud: data.fraud || {}
      };
    },

    is_aml_service(result) {
      const label = (result.label || "").toLowerCase();
      return label.includes("aml") || label.includes("anti-money");
    },

    get_risk_alert_class(risk_score) {
      if (!risk_score) return "alert-secondary";
      const risk = risk_score.toLowerCase();
      if (risk.includes("high")) return "alert-danger";
      if (risk.includes("medium")) return "alert-warning";
      return "alert-success";
    },

    normalize_status(status) {
      if (!status) return "fail";
      const verification_status = status.toString().toLowerCase();

      if (["success", "pass", "verified", "ok"].includes(verification_status)) return "success";
      if (["fail", "failed", "error"].includes(verification_status)) return "fail";

      return verification_status; // fallback
    },

    is_nested_object(obj) {
      return (
        obj &&
        typeof obj === "object" &&
        !Array.isArray(obj) &&
        Object.keys(obj).length > 0
      );
    },

    filtered_detail_entries(obj) {
      if (!obj || typeof obj !== "object") return {};
      // Removes common meta keys that don't need to be displayed in the nested section
      return Object.fromEntries(
        Object.entries(obj).filter(([key]) => key !== "field_results" && key !== "message")
      );
    },

    async fetch_verification_services() {
      this.loading_services = true;
      try {
        const response = await axios.get(GET_PROJECTS_ASSIGNED_TO_SERVICE, {
          headers: this.headers,
          params: { project_id: this.project_id },
        });
        const parsed = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        if (parsed.status === 'success' && parsed.data) {
          this.verification_services = parsed.data.map(service => ({
            id: service.id,
            name: service.name,
            description: service.description,
            is_active: service.is_active
          }));
        } else this.show_error(parsed.message || 'Failed to fetch services');
      } catch {
        this.show_error('Failed to load verification services.');
      } finally {
        this.loading_services = false;
      }
    },

    async on_service_change() {
      if (!this.selected_service_id) return;
      await this.fetch_verification_results();
    },

    // Fetch results for the selected service using its dedicated API
    async fetch_verification_results() {
      if (!this.resolved_entity_profile_id || !this.selected_service_id)
        return;

      this.loading_results = true;
      this.api_verification_results = [];

      try {
        const service = this.selected_service;
        const name = service.name.toLowerCase();

        let api_call = null;

        // Dynamically pick the API based on the selected service
        if (name.includes("kyc")) {
          api_call = getKycVerificationResults;
        } else if (name.includes("aml")) {
          api_call = getAMLVerificationResults;
        } else if (name.includes("id")) {
          api_call = getIDVVerificationResults;
        } else if (name.includes("cipc")) {
          api_call = getCIPCVerificationResults;
        } else if (name.includes("credit")) {
          api_call = getCreditReportVerificationResults;
        }

        if (!api_call) {
          this.loading_results = false;
          return;
        }

        // Call only the matching API
        const api_result = await api_call({
          entity_profile_id: this.resolved_entity_profile_id,
          service_id: this.selected_service_id,
        });

        // Process only this API’s results
        if (api_result) {
          this.api_verification_results = this.process_single_api_response(
            api_result,
            service.name,
            service.name
          );
        }
      } catch (error) {
        console.error("Error fetching verification results:", error);
      } finally {
        this.loading_results = false;
      }
    },

    process_all_api_responses(results, service_name) {
      const all_results = [];

      results.forEach((result, index) => {
        if (result.status === "fulfilled" && result.value) {
          const api_result = result.value;
          const api_names = ["KYC", "AML", "IDV", "CIPC", "Credit Report"];
          const api_name = api_names[index] || `API-${index + 1}`;

          if (["success", "fail"].includes(api_result.status)) {
            const processed_results = this.process_single_api_response(api_result, service_name, api_name);
            if (processed_results && processed_results.length > 0) all_results.push(...processed_results);
          }
        }
      });

      // Show only results that match the selected service
      this.api_verification_results = all_results.filter(result => {
        const selected = (this.selected_service?.name || "").toLowerCase();
        const label = (result.label || "").toLowerCase();
        return (
          label.includes(selected) ||
          selected.includes(label.split(" ")[0])
        );
      });
    },

    process_single_api_response(api_result, service_name, api_name) {
      if (api_result.results && api_result.results.length > 0) {
        return api_result.results.map((r, i) => ({
          key: `${api_name}-${this.selected_service_id}-${i}`,
          label: `${service_name} - ${api_name} Results`,
          date: new Date().toLocaleDateString(),
          status: r.status || api_result.status || "success",
          details: this.extract_details_from_result(r),
        }));
      }

      const details = this.extract_details_from_result(api_result.raw_data || api_result);
      return [{
        key: `${api_name}-${this.selected_service_id}-0`,
        label: `${service_name} - ${api_name} Results`,
        date: new Date().toLocaleDateString(),
        status: this.normalize_status(api_result.status),
        details,
      }];
    },

    extract_details_from_result(result_item) {
      // Directly return if it already has field_results or results_data
      if (result_item.results_data || result_item.field_results) {
        return result_item;
      }

      // If raw_data is nested inside result_item
      if (result_item.raw_data && (result_item.raw_data.results_data || result_item.raw_data.data)) {
        return {
          field_results: result_item.raw_data.field_results || {},
          results_data:
            result_item.raw_data.results_data ||
            result_item.raw_data.data ||
            result_item.raw_data.details ||
            {},
        };
      }

      // Fallback paths for inconsistent APIs
      if (result_item.data && (result_item.data.results_data || result_item.data.details)) {
        return {
          field_results: result_item.data.field_results || {},
          results_data:
            result_item.data.results_data ||
            result_item.data.details ||
            result_item.data ||
            {},
        };
      }

      // Final fallback
      return {
        field_results: result_item.field_results || {},
        results_data:
          result_item.results_data ||
          result_item.details ||
          result_item.data ||
          {},
      };
    },

    toggle_tab(tab) {
      this.active_tabs = this.active_tabs.includes(tab)
        ? this.active_tabs.filter(t => t !== tab)
        : [...this.active_tabs, tab];
    },

    toggle_program() { this.is_trust_form_open = !this.is_trust_form_open; },
    toggle_expanded(index) { this.expanded_index = this.expanded_index === index ? null : index; },

    format_key(str) {
      if (typeof str !== 'string') return String(str || '');
      return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    format_value(value) {
      if (value === null || value === undefined) return "N/A";
      if (typeof value === "boolean") return value ? "Yes" : "No";
      if (typeof value === "object") return value; // keep as object for recursive render
      return String(value);
    },

    is_complex_object(val) {
      return val && typeof val === "object" && Object.keys(val).length > 0;
    },


    get_status_badge_class(status) {
      const s = (status || '').toLowerCase();
      const map = { success: 'success', fail: 'failed', error: 'failed', pending: 'pending' };
      return `status-badge ${map[s] || 'default'}`;
    },

    async get_forms() {
      try {
        const { data: res } = await axios.get(GET_APPLICATION_FORMS, {
          headers: this.headers,
          params: { application_id: this.application_id },
        });
        const rows = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
        this.form_name = rows[0]?.form?.form_name ?? "";
      } catch (e) {
        this.show_error("Error loading forms: " + (e.response?.data?.message || e.message));
      }
    },

    initialize_data_table() {
      this.$nextTick(() => {
        const table = $('#verification_logs_table');
        if ($.fn.DataTable.isDataTable(table)) {
          table.DataTable().destroy();
        }

        table.DataTable({
          responsive: true,
          emptyTable: "No data available in table",
          language: {
            info: "",
            lengthMenu: "_MENU_&nbsp;&nbsp;Entries per page"
          }
        });
      });
    },

    get_verification_logs() {
      axios.get(GET_VERIFICATION_LOGS, {
        headers: this.headers,
        params: { entity_profile_id: this.resolved_entity_profile_id }
      })
      .then(response => {
        const parsed_response = response.data;

        if (parsed_response.status === "success") {
          this.verification_logs = parsed_response.logs || [];
          this.$nextTick(() => this.initialize_data_table());

        } else {
          this.show_error(parsed_response.message || "No logs found");
        }
      })
      .catch(error => {
        this.show_error("Error fetching verification logs: " + error);
      });
    },

    open_verification_logs() {
      this.get_verification_logs();
    },

    get_service_name(service_id) {
      const service = this.verification_services.find(s => s.id === service_id);
      return service ? service.name : `Service ${service_id}`;
    },

    get_display_message(message) {
      const match = message.match(/\[(.*?)\]/);
      if (match) {
      const group = Reflect.get(match, '1');
      const fields = group.split(',').map(f => f.trim().replace(/['"]/g, ''));
        return `Missing required AML fields: ${fields.join(', ')}`;
      }
      return message;
    }
  },
};
</script>